/**
 * Live preview for a column resize drag.
 *
 * Committing the width to React state on every pointer frame re-renders the
 * page that owns the layout (filters, toolbar, every row), which is far too
 * expensive at 60fps. Instead the drag writes the width straight to the DOM and
 * the layout is committed once on release.
 *
 * This is safe because React only rewrites `style.width` when the *rendered*
 * value changes: rc-table keeps rendering the pre-drag width until we commit,
 * so unrelated re-renders leave these inline writes alone, and the commit
 * replaces them with the same value.
 */

/** Sections rc-table renders inside a table container, as direct children. */
const SECTION_TABLE_SELECTOR = [
  ':scope > .ant-table-header > table',
  ':scope > .ant-table-body > table',
  ':scope > .ant-table-content > table',
].join(', ');

type ResizeTarget = {
  table: HTMLTableElement;
  /** Only the body/content colgroup holds static widths; the fixed header's is measured. */
  col: HTMLTableColElement | null;
  startTableWidth: number;
};

type DataTableColumnResizePreview = {
  update: (width: number) => void;
};

function readTableWidth(table: HTMLTableElement): number {
  const inline = Number.parseFloat(table.style.width);
  if (Number.isFinite(inline)) return inline;
  return table.getBoundingClientRect().width;
}

function readColumn(
  table: HTMLTableElement,
  columnIndex: number,
): HTMLTableColElement | null {
  // The fixed header derives its colgroup from measured widths, so writing
  // there would fight React; it follows the body via rc-table's measure row.
  if (table.parentElement?.classList.contains('ant-table-header')) return null;
  const col = table.querySelector('colgroup')?.children[columnIndex];
  return col instanceof HTMLTableColElement ? col : null;
}

/**
 * Captures the tables affected by dragging `headerCell` and returns a writer
 * that resizes the column without touching React. Returns `null` when the
 * table structure is not resizable in place, so the caller can fall back to
 * committing on release only.
 */
export function createDataTableColumnResizePreview(
  headerCell: HTMLTableCellElement,
  startWidth: number,
): DataTableColumnResizePreview | null {
  const container = headerCell.closest('.ant-table-container');
  if (!container) return null;

  const columnIndex = headerCell.cellIndex;
  const targets: ResizeTarget[] = [
    ...container.querySelectorAll<HTMLTableElement>(SECTION_TABLE_SELECTOR),
  ].map((table) => ({
    table,
    col: readColumn(table, columnIndex),
    startTableWidth: readTableWidth(table),
  }));

  if (!targets.some((target) => target.col)) return null;

  return {
    update: (width) => {
      const delta = width - startWidth;
      targets.forEach(({ table, col, startTableWidth }) => {
        if (col) col.style.width = `${width}px`;
        // Without this the table keeps its old total and the browser
        // redistributes the difference across the other columns.
        if (startTableWidth) {
          table.style.width = `${startTableWidth + delta}px`;
        }
      });
    },
  };
}
